import { useMemo } from 'react'
import { FeatureType } from '@numeo/types-core'
import { useAuth } from './auth'
import { isFeatureEnabled } from 'utils/feature-checker'

/**
 * The three primary user roles that drive sidebar layout & navigation.
 *
 * Mirrors the reference design (`new-agent-numeo-main`) which presents three
 * distinct sidebars depending on what the customer signed up for:
 *
 *   - `dispatcher` → Numeo Spot / Extension users. Sees a flat list focused
 *     on load search (AI Hub, Load Hub, Load Radar) plus Profiles.
 *   - `updater`    → Updater Agent users. Sees Dashboard + Manage Loads +
 *     Profiles, optimised for active dispatching.
 *   - `tmc`        → Trucking Management Company (TMS) accounts. Sees the
 *     full enterprise menu — Accounting, Fleet Management, Safety, Reports,
 *     and the dispatcher / updater menus combined.
 */
export type UserRole = 'dispatcher' | 'updater' | 'tmc'

export interface UserRoleInfo {
    /** The currently active role used to filter the sidebar */
    role: UserRole
    /** All roles the user has access to (used by future role switcher) */
    availableRoles: UserRole[]
    /** True when the user is configured as a Numeo Spot / Extension user */
    isDispatcher: boolean
    /** True when the Updater agent (full or lite) is enabled */
    isUpdater: boolean
    /** True when this account has full TMS capabilities (Accounting/Fleet/Safety) */
    isTms: boolean
}

/**
 * Derive the user's primary role from feature flags + user.type.
 *
 * Priority order is intentional: TMS supersedes the others because a TMS
 * customer almost always has Updater + Dispatcher features enabled too,
 * but they need the full enterprise sidebar by default.
 */
export const useUserRole = (): UserRoleInfo => {
    const { user, application } = useAuth()

    return useMemo(() => {
        const isExtensionUser = user?.type === 'EXTENSION'
        const isExtensionFeature = isFeatureEnabled(application, FeatureType.Extension)
        const isUpdaterFeature = isFeatureEnabled(application, FeatureType.Updater) || isFeatureEnabled(application, FeatureType.UpdaterLite)
        const isAccountingFeature = isFeatureEnabled(application, FeatureType.Accounting)
        const isFleetFeature = isFeatureEnabled(application, FeatureType.Fleet)
        const isSafetyFeature = isFeatureEnabled(application, FeatureType.Safety)

        const isDispatcher = isExtensionUser || isExtensionFeature
        const isUpdater = isUpdaterFeature
        const isTms = isAccountingFeature || isFleetFeature || isSafetyFeature

        const availableRoles: UserRole[] = []
        if (isDispatcher) availableRoles.push('dispatcher')
        if (isUpdater) availableRoles.push('updater')
        if (isTms) availableRoles.push('tmc')

        // Pick a single primary role for the sidebar layout.
        // TMS wins because it has the richest menu.
        let role: UserRole
        if (isTms) role = 'tmc'
        else if (isUpdater) role = 'updater'
        else role = 'dispatcher'

        return {
            role,
            availableRoles: availableRoles.length > 0 ? availableRoles : ['dispatcher'],
            isDispatcher,
            isUpdater,
            isTms,
        }
    }, [user?.type, application])
}
